(function () {
  'use strict';

  const page = (window.FoxTheme && window.FoxTheme.page) || {};

  const STORAGE_KEY = 'seo_freshness_v1';
  const ENGAGEMENT_THRESHOLD = 10; // Number of engaged visits to mark as "reviewed"
  const MIN_SCROLL_DEPTH = 0.75; // 75% scroll
  const MIN_DWELL_TIME = 30; // 30 seconds
  const DATA_RETENTION_DAYS = 180;

  function getData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : { pages: {} };
    } catch {
      return { pages: {} };
    }
  }

  function saveData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      console.warn('[Content Freshness] Storage failed');
    }
  }

  // Track engagement metrics for current page
  function trackEngagement() {
    const pageKey = window.location.pathname;
    const data = getData();

    if (!data.pages[pageKey]) {
      data.pages[pageKey] = {
        visits: 0,
        engagedVisits: 0,
        lastReviewed: null,
        reviewThreshold: ENGAGEMENT_THRESHOLD,
        firstSeen: Date.now(),
      };
    }

    const pageData = data.pages[pageKey];
    pageData.visits++;

    // Track engagement
    const startTime = Date.now();
    let maxScroll = 0;
    let hasInteracted = false;

    // Scroll tracking rides the shared coordinator (assets/scroll-coordinator.js) instead
    // of its own unthrottled listener. The old handler fired 60-100+ times a second on a
    // touch scroll and read document.documentElement.scrollHeight on every one of them -
    // exactly the expensive measurement the coordinator caches behind a ResizeObserver -
    // re-dirtying geometry the coordinator had just measured cleanly. maxScroll is only
    // ever read once, at exit (evaluateEngagement), so sampling once per frame instead of
    // once per raw event cannot lower the recorded peak.
    window.CoraScroll.subscribe({
      name: 'content-freshness-scroll',
      read: (m) => (m.scrollY + m.innerHeight) / m.scrollHeight,
      write: (scrollDepth) => {
        maxScroll = Math.max(maxScroll, scrollDepth);
      },
    });

    // Interaction tracking
    const interactions = ['click', 'touchstart', 'keydown', 'submit'];
    const onInteraction = () => {
      hasInteracted = true;
    };

    interactions.forEach((event) => {
      document.addEventListener(event, onInteraction, { once: true, passive: true });
    });

    // Evaluate engagement on page exit
    const evaluateEngagement = () => {
      const dwellTime = (Date.now() - startTime) / 1000;

      // Check if visit was "engaged"
      const isEngaged =
        maxScroll >= MIN_SCROLL_DEPTH && dwellTime >= MIN_DWELL_TIME && hasInteracted;

      if (isEngaged) {
        pageData.engagedVisits++;

        // Check if we've hit threshold to mark as "reviewed"
        if (pageData.engagedVisits >= pageData.reviewThreshold) {
          pageData.lastReviewed = new Date().toISOString();

          // Use exponential backoff for next review
          // First review: 10 visits, next: 25, then: 50, 100, etc.
          pageData.reviewThreshold = Math.ceil(pageData.reviewThreshold * 2.5);
        }
      }

      // Cleanup old pages
      const cutoff = Date.now() - DATA_RETENTION_DAYS * 24 * 60 * 60 * 1000;
      Object.keys(data.pages).forEach((key) => {
        if (data.pages[key].firstSeen < cutoff) {
          delete data.pages[key];
        }
      });

      saveData(data);
    };

    window.addEventListener('beforeunload', evaluateEngagement);
    window.addEventListener('pagehide', evaluateEngagement);

    // Also evaluate after 2 minutes for long sessions
    setTimeout(evaluateEngagement, 120000);
  }

  // Update schema dates based on freshness data
  function updateSchemaDates() {
    const pageKey = window.location.pathname;
    const data = getData();
    const pageData = data.pages[pageKey];

    if (!pageData || !pageData.lastReviewed) return;

    const lastReviewed = pageData.lastReviewed;
    const dateModified = lastReviewed;

    // Find all JSON-LD scripts
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');

    scripts.forEach((script) => {
      try {
        const json = JSON.parse(script.textContent);

        // Update different schema types
        if (json['@type'] === 'Product') {
          updateProductSchema(json, dateModified, lastReviewed);
          script.textContent = JSON.stringify(json);
        } else if (json['@type'] === 'BlogPosting' || json['@type'] === 'Article') {
          updateArticleSchema(json, dateModified, lastReviewed);
          script.textContent = JSON.stringify(json);
        } else if (json['@type'] === 'WebPage') {
          updateWebPageSchema(json, dateModified, lastReviewed);
          script.textContent = JSON.stringify(json);
        } else if (json['@graph']) {
          // Handle @graph format
          json['@graph'].forEach((item) => {
            if (item['@type'] === 'Product') {
              updateProductSchema(item, dateModified, lastReviewed);
            } else if (item['@type'] === 'BlogPosting' || item['@type'] === 'Article') {
              updateArticleSchema(item, dateModified, lastReviewed);
            } else if (item['@type'] === 'WebPage') {
              updateWebPageSchema(item, dateModified, lastReviewed);
            }
          });
          script.textContent = JSON.stringify(json);
        }
      } catch {
        // Skip invalid JSON
      }
    });

    // Add ReviewAction schema to show content is actively reviewed
    addReviewActionSchema(lastReviewed);
  }

  function updateProductSchema(schema, dateModified, lastReviewed) {
    // Add modification date
    if (!schema.dateModified || new Date(schema.dateModified) < new Date(dateModified)) {
      schema.dateModified = dateModified;
    }

    // Add review information
    if (!schema.review) {
      schema.review = [];
    }

    schema.review.push({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
      },
      author: {
        '@type': 'Organization',
        name: page.shopName,
      },
      datePublished: lastReviewed,
      reviewBody: 'Content reviewed and validated for accuracy',
    });
  }

  function updateArticleSchema(schema, dateModified, _lastReviewed) {
    schema.dateModified = dateModified;

    // Add editorial review
    if (!schema.reviewedBy) {
      schema.reviewedBy = {
        '@type': 'Organization',
        name: page.shopName,
      };
    }
  }

  function updateWebPageSchema(schema, dateModified, lastReviewed) {
    schema.dateModified = dateModified;
    schema.lastReviewed = lastReviewed;

    // Add review action
    if (!schema.potentialAction) {
      schema.potentialAction = [];
    }

    schema.potentialAction.push({
      '@type': 'ReviewAction',
      agent: {
        '@type': 'Organization',
        name: page.shopName,
      },
      startTime: lastReviewed,
      result: {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
        },
      },
    });
  }

  function addReviewActionSchema(lastReviewed) {
    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'ReviewAction',
      actionStatus: 'CompletedActionStatus',
      agent: {
        '@type': 'Organization',
        name: page.shopName,
      },
      object: {
        '@type': 'WebPage',
        url: window.location.href,
      },
      startTime: lastReviewed,
      endTime: lastReviewed,
      result: {
        '@type': 'Review',
        reviewBody: 'Content has been reviewed and validated for accuracy and relevance',
        datePublished: lastReviewed,
        author: {
          '@type': 'Organization',
          name: page.shopName,
        },
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'content-freshness-review';
    script.textContent = JSON.stringify(reviewSchema);
    document.head.appendChild(script);
  }

  // Add meta tag for last reviewed date
  function addLastReviewedMeta() {
    const pageKey = window.location.pathname;
    const data = getData();
    const pageData = data.pages[pageKey];

    if (!pageData || !pageData.lastReviewed) return;

    const meta = document.createElement('meta');
    meta.name = 'last-reviewed';
    meta.content = pageData.lastReviewed;
    document.head.appendChild(meta);
  }

  // Initialize
  function init() {
    trackEngagement();

    // Update schemas after page loads
    setTimeout(() => {
      updateSchemaDates();
      addLastReviewedMeta();
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
